'use client'

import * as React from 'react'
import { Instagram, Music2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SamplePreview } from '@/components/tools/widget/sample-preview'
import { demoPostsFor } from '@/lib/copy/demo-products'
import { DEFAULT_WIDGET_CONFIG, type WidgetSource, type WidgetView } from '@/lib/widget/embed'

// The homepage band's working preview: the same client renderer the
// flagship /tools/widget-builder uses, cut down to the two free sources
// and four layouts. Every control updates the preview immediately, so
// the band's "watch the live preview update as you go" line is literally
// true on the homepage itself. The full builder (customise sliders,
// live-post fetch, provisioning) stays one click away.

const SOURCES: {
  value: WidgetSource
  label: string
  icon: React.ComponentType<{ className?: string }>
  demoSlug: string
}[] = [
  { value: 'instagram', label: 'Instagram', icon: Instagram, demoSlug: 'fashion' },
  { value: 'tiktok', label: 'TikTok', icon: Music2, demoSlug: 'beauty' },
]

const LAYOUTS: { value: WidgetView; label: string }[] = [
  { value: 'grid', label: 'Grid' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'stories', label: 'Stories' },
  { value: 'reelCarousel', label: 'Reels' },
]

export function BuilderMini() {
  const [source, setSource] = React.useState<WidgetSource>('instagram')
  const [view, setView] = React.useState<WidgetView>('grid')
  const [reduced, setReduced] = React.useState(false)

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const demoSlug = SOURCES.find((s) => s.value === source)?.demoSlug ?? 'fashion'
  const posts = React.useMemo(() => demoPostsFor(demoSlug), [demoSlug])
  const config = React.useMemo(
    () => ({ ...DEFAULT_WIDGET_CONFIG, source, view, columns: 3, mobileColumns: 2 }),
    [source, view],
  )

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <div
          className="inline-flex rounded-full border bg-background p-0.5"
          role="group"
          aria-label="Source"
        >
          {SOURCES.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => setSource(value)}
              aria-pressed={source === value}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                source === value
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
            </button>
          ))}
        </div>
        <div
          className="inline-flex rounded-full border bg-background p-0.5"
          role="group"
          aria-label="Layout"
        >
          {LAYOUTS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setView(value)}
              aria-pressed={view === value}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                view === value
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        <SamplePreview config={config} posts={posts} reducedMotion={reduced} showHotspots={false} />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Sample content.</p>
    </div>
  )
}
